import { FC, useCallback, useEffect, useRef } from "react";
import { OptionProps } from "react-select";
import { Checkbox } from "capitalrx-components-2.0/lib/forms/Checkbox";
import { CustomReactSelectProps } from "capitalrx-components-2.0/lib/forms/Dropdown/CustomReactSelectProps";
import { OptionItem } from "capitalrx-components-2.0/lib/forms/Dropdown/OptionItem";
import classNames from "classnames";
import styles from "./styles.module.scss";

/**
 * A single option within the select.
 */
export const Option: FC<OptionProps<OptionItem | null>> = (props) => {
  const { children, innerProps, innerRef, isFocused, isSelected, selectProps, data } = props;
  const { enableCheckboxOptions, isItemSelected, onRemove } = selectProps as CustomReactSelectProps;

  const ref = useRef<HTMLDivElement | null>(null);
  const optionValue = data?.value;

  const optionClassName = classNames(styles.Option, "option", {
    focused: isFocused,
    selected: isSelected,
  });

  const handleRef = useCallback((value: HTMLDivElement | null) => {
    ref.current = value;
    innerRef?.(value);
  }, []);

  const handleRemove = useCallback(() => {
    onRemove?.(optionValue);
  }, [optionValue, onRemove]);

  useEffect(() => {
    if (!isFocused) return;
    if (!ref.current) return;

    let current: HTMLElement | null = ref.current;
    while (current) {
      if (current.classList.contains("menu")) break;
      current = current.parentElement;
    }

    if (!current || current === ref.current) return;

    const menuBounds = current.getBoundingClientRect();
    const optionBounds = ref.current.getBoundingClientRect();

    if (optionBounds.top < menuBounds.top || optionBounds.bottom > menuBounds.bottom) {
      if (optionBounds.top < menuBounds.top) {
        current.scrollBy(0, optionBounds.top - menuBounds.top);
      } else if (optionBounds.bottom > menuBounds.bottom) {
        current.scrollBy(0, optionBounds.bottom - menuBounds.bottom);
      }
    }
  }, [isFocused]);

  return (
    <div
      {...innerProps}
      ref={handleRef}
      className={optionClassName}
      role="option"
      aria-selected={enableCheckboxOptions ? isItemSelected?.(optionValue) ?? false : isSelected}
    >
      <div className={classNames(styles.Content, "content", { focused: isFocused })} onClick={handleRemove}>
        {enableCheckboxOptions && (
          <Checkbox
            value={isItemSelected?.(optionValue) ?? false}
            onChange={handleRemove}
            unpadded
            hideLabel
          />
        )}
        {children}
      </div>
    </div>
  );
};
